import { Download, FileSpreadsheet, RefreshCw } from "lucide-react";
import {
  formatInterviewDate,
  formatInterviewTime,
} from "../../../components/interviewList/schedule";
import type { StakeholderAnswer } from "../../../hooks";

interface StakeholderAnswerSectionProps {
  stakeholderAnswers: readonly StakeholderAnswer[];
  loading: boolean;
  /** 読み込みに失敗したか。失敗を「まだ回答がない」と読ませないために区別する */
  loadFailed: boolean;
  working: boolean;
  onImport: (answerIds: string[]) => void;
  onCreateSheets: () => void;
}

function EmptyCell() {
  return <span className="text-[#9ca3af] text-[13px]">-</span>;
}

function SheetLinkCell({ url, label }: { url: string | null; label: string }) {
  if (!url) {
    // 複製に失敗した回答。シートの作成をやり直すまで取り込めない
    return <span className="text-[#b45309] text-[13px]">未作成</span>;
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="text-[var(--color-primary-500)] text-sm hover:underline"
    >
      {label}
    </a>
  );
}

function InterviewedAtCell({
  interviewedAt,
}: {
  interviewedAt: string | null;
}) {
  if (!interviewedAt) return <EmptyCell />;
  return (
    <>
      <div className="text-sm">{formatInterviewDate(interviewedAt)}</div>
      <div className="text-[13px] text-[var(--color-text-secondary)]">
        {formatInterviewTime(interviewedAt)}
      </div>
    </>
  );
}

function ImportStatusCell({
  importedAnswerCount,
}: {
  importedAnswerCount: number;
}) {
  if (importedAnswerCount === 0) {
    return (
      <span className="rounded-full bg-[var(--color-bg-subtle-gray)] px-2.5 py-1 font-medium text-[12px] text-[var(--color-text-secondary)]">
        未取り込み
      </span>
    );
  }
  return (
    <span className="whitespace-nowrap rounded-full bg-[#dcfce7] px-2.5 py-1 font-medium text-[#166534] text-[12px]">
      取り込み済み（{importedAnswerCount}問）
    </span>
  );
}

export function StakeholderAnswerSection({
  stakeholderAnswers,
  loading,
  loadFailed,
  working,
  onImport,
  onCreateSheets,
}: StakeholderAnswerSectionProps) {
  const importableAnswers = stakeholderAnswers.filter(
    (answer) => answer.contentsSheetUrl,
  );
  const missingSheetCount =
    stakeholderAnswers.length - importableAnswers.length;

  return (
    <div className="overflow-hidden rounded-xl border border-[var(--color-border-default)] bg-white shadow-[0_2px_8px_rgba(0,0,0,0.05)]">
      <div className="flex flex-wrap items-center justify-between gap-4 border-[var(--color-primary-500)] border-b-2 bg-[var(--color-bg-subtle-gray)] px-6 py-5">
        <div>
          <h2 className="m-0 font-bold text-2xl text-[var(--color-primary-500)]">
            回答
          </h2>
          <p className="mt-1 mb-0 text-[13px] text-[var(--color-text-secondary)]">
            実施後に要約シートへ書いた内容を取り込みます。レポートの元データになります
          </p>
          {missingSheetCount > 0 && (
            <p className="mt-1 mb-0 text-[#b45309] text-[13px]">
              {`要約シートが作られていない関係性が${missingSheetCount}件あります。シートの作成をやり直してください`}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={onCreateSheets}
            disabled={working}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-md border px-4 py-2 font-medium text-sm ${
              working
                ? "cursor-not-allowed border-[var(--color-border-default)] text-[#9ca3af]"
                : "cursor-pointer border-[var(--color-primary-500)] text-[var(--color-primary-500)] hover:bg-[var(--color-bg-subtle-gray)]"
            }`}
          >
            <RefreshCw className="h-4 w-4" />
            シートの作成をやり直す
          </button>
          <button
            type="button"
            onClick={() =>
              onImport(importableAnswers.map((answer) => answer.answerId))
            }
            disabled={importableAnswers.length === 0 || working}
            className={`flex items-center gap-1.5 whitespace-nowrap rounded-md border-none px-4 py-2 font-medium text-sm text-white ${
              importableAnswers.length === 0 || working
                ? "cursor-not-allowed bg-[#9ca3af]"
                : "cursor-pointer bg-[var(--color-primary-500)] hover:bg-[#045a94]"
            }`}
          >
            <Download className="h-4 w-4" />
            要約シートから取り込む
          </button>
        </div>
      </div>
      {loading ? (
        <div className="p-12 text-center font-medium text-[var(--color-text-secondary)]">
          読み込み中...
        </div>
      ) : loadFailed ? (
        <div className="p-12 text-center">
          <FileSpreadsheet className="mx-auto mb-4 h-12 w-12 text-[#9ca3af]" />
          <p className="text-[var(--color-text-secondary)]">
            回答を読み込めませんでした
          </p>
        </div>
      ) : stakeholderAnswers.length === 0 ? (
        <div className="p-12 text-center">
          <FileSpreadsheet className="mx-auto mb-4 h-12 w-12 text-[#9ca3af]" />
          <p className="text-[var(--color-text-secondary)]">
            回答がまだありません。先に関係性を入稿してください
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[var(--color-bg-subtle-gray)]">
                {[
                  "回答者",
                  "受け取る人",
                  "インタビュー設定",
                  "実施日",
                  "設問シート",
                  "要約シート",
                  "取り込み",
                  "操作",
                ].map((label) => (
                  <th
                    key={label}
                    className="whitespace-nowrap border-[var(--color-border-default)] border-b px-4 py-3 text-left font-semibold text-[13px] text-[var(--color-text-secondary)]"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stakeholderAnswers.map((answer) => (
                <tr
                  key={answer.answerId}
                  className="border-[var(--color-border-default)] border-b"
                >
                  <td className="px-4 py-4 text-sm">
                    <div className="font-medium text-[var(--color-text-primary)]">
                      {answer.respondentName}
                    </div>
                    <div className="text-[13px] text-[var(--color-text-secondary)]">
                      {answer.respondentEmail}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm">
                    <div className="font-medium text-[var(--color-text-primary)]">
                      {answer.recipientName}
                    </div>
                    <div className="text-[13px] text-[var(--color-text-secondary)]">
                      {answer.recipientEmail}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-[var(--color-text-secondary)] text-sm">
                    {answer.stakeholderSettingName}
                  </td>
                  <td className="whitespace-nowrap px-4 py-4">
                    <InterviewedAtCell interviewedAt={answer.interviewedAt} />
                  </td>
                  <td className="whitespace-nowrap px-4 py-4">
                    <SheetLinkCell url={answer.scriptSheetUrl} label="設問" />
                  </td>
                  <td className="whitespace-nowrap px-4 py-4">
                    <SheetLinkCell url={answer.contentsSheetUrl} label="要約" />
                  </td>
                  <td className="whitespace-nowrap px-4 py-4">
                    <ImportStatusCell
                      importedAnswerCount={answer.importedAnswerCount}
                    />
                  </td>
                  <td className="whitespace-nowrap px-4 py-4 text-sm">
                    {answer.contentsSheetUrl ? (
                      <button
                        type="button"
                        onClick={() => onImport([answer.answerId])}
                        disabled={working}
                        className={`border-none bg-none p-0 text-[var(--color-primary-500)] ${
                          working
                            ? "cursor-not-allowed text-[#9ca3af]"
                            : "cursor-pointer hover:underline"
                        }`}
                      >
                        取り込む
                      </button>
                    ) : (
                      <span className="text-[#9ca3af] text-[13px]">
                        要約シートが未作成です
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
